/**
 * M1 后台管理 API 封装（全部走真实接口，前缀 /api/admin/v1）。
 *
 * 对应后端控制器：
 * - admin-tag.controller.ts / admin-kol.controller.ts / admin-file.controller.ts
 * - admin-content.controller.ts / admin-topic.controller.ts
 * - admin-home.controller.ts / admin-import.controller.ts
 */
import type { ApiMeta } from '@liyu/shared-types';
import { apiDownload, apiRequest, getAccessToken } from './client';
import { AbortError, sha256HexOfBlob, type ChunkedHashOptions } from './sha256-chunked';
import type {
  KolApplicationRecord,
  KolArticleAuditRow,
  KolInviteRecord,
} from './types';
import type {
  AdminApproveBorrowingPayload,
  AdminAuthorPayload,
  AdminAuthorRow,
  AdminBookCopyListQuery,
  AdminBookCopyPayload,
  AdminBookCopyRow,
  AdminBorrowingListQuery,
  AdminBorrowingListResult,
  AdminBorrowingRow,
  AdminBorrowingTaskKind,
  AdminBorrowingTaskResult,
  AdminBorrowPolicy,
  AdminBorrowPolicyUpdatePayload,
  AdminDonorPayload,
  AdminDonorRow,
  AdminMembershipPlan,
  AdminPickupLocationPayload,
  AdminPickupLocationRow,
  AdminRefundListQuery,
  AdminRefundListResult,
  AdminRefundRow,
  AdminReservationListQuery,
  AdminReservationRow,
  AdminTicketTypeListResult,
  AdminTicketTypeRow,
  ContentAccessScope,
  ContentCreatePayload,
  ContentDetail,
  ContentDownloadAccess,
  ContentListRow,
  QuickCreatedBook,
  ContentPreviewPolicy,
  ContentResourceKind,
  ContentType,
  ContentUpdatePayload,
  ContentValidationResult,
  ContentViewAccess,
  CopyrightStatus,
  FilePurpose,
  HomeItemInput,
  HomeModuleRow,
  HomeModuleType,
  ImportJobDetail,
  ImportJobRow,
  KolRecord,
  MembershipPlanUpsertPayload,
  Paginated,
  PlanBenefitReplaceInput,
  TagGroup,
  TagRecord,
  TicketTypeUpsertPayload,
  TopicDetail,
  TopicListRow,
  TopicThemeStyle,
  UploadedFileMeta,
  UploadIntentResult,
  UserSearchResult,
} from './types';

function toPaginated<T>(data: T[], meta?: ApiMeta, fallbackSize = 20): Paginated<T> {
  return {
    items: data,
    total: meta?.total ?? data.length,
    page: meta?.page ?? 1,
    pageSize: meta?.pageSize ?? fallbackSize,
  };
}

export interface PageQuery {
  page?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// 标签
// ---------------------------------------------------------------------------

export interface TagListQuery extends PageQuery {
  group?: TagGroup;
  keyword?: string;
  includeInactive?: boolean;
}

export async function listTags(query: TagListQuery = {}): Promise<Paginated<TagRecord>> {
  const result = await apiRequest<TagRecord[]>('/tags', {
    query: {
      page: query.page,
      pageSize: query.pageSize,
      group: query.group,
      keyword: query.keyword,
      includeInactive:
        query.includeInactive === undefined ? undefined : String(query.includeInactive),
    },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function createTag(body: {
  group: TagGroup;
  displayName: string;
  sortOrder?: number;
}): Promise<TagRecord> {
  return (await apiRequest<TagRecord>('/tags', { method: 'POST', body })).data;
}

export async function updateTag(
  id: string,
  body: { displayName?: string; sortOrder?: number; isActive?: boolean },
): Promise<TagRecord> {
  return (await apiRequest<TagRecord>(`/tags/${id}`, { method: 'PATCH', body })).data;
}

export async function setTagActivation(id: string, isActive: boolean): Promise<TagRecord> {
  return (
    await apiRequest<TagRecord>(`/tags/${id}/activation`, { method: 'PATCH', body: { isActive } })
  ).data;
}

export async function deleteTag(id: string): Promise<{ mode: string }> {
  return (await apiRequest<{ mode: string }>(`/tags/${id}`, { method: 'DELETE' })).data;
}

// ---------------------------------------------------------------------------
// KOL
// ---------------------------------------------------------------------------

export async function listKols(
  query: PageQuery & { keyword?: string } = {},
): Promise<Paginated<KolRecord>> {
  const result = await apiRequest<KolRecord[]>('/kols', {
    query: { page: query.page, pageSize: query.pageSize, keyword: query.keyword },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export interface KolCreatePayload {
  liyuNo: string;
  displayName: string;
  avatarUrl?: string;
  bio?: string;
  expertise?: string[];
  certificationName?: string;
  certificationNote?: string;
  tags?: string[];
  isVisible?: boolean;
  sortOrder?: number;
}

export async function createKol(body: KolCreatePayload): Promise<KolRecord> {
  return (await apiRequest<KolRecord>('/kols', { method: 'POST', body })).data;
}

export async function getKol(id: string): Promise<KolRecord> {
  return (await apiRequest<KolRecord>(`/kols/${id}`)).data;
}

export async function updateKol(
  id: string,
  body: Omit<KolCreatePayload, 'liyuNo'>,
): Promise<KolRecord> {
  return (await apiRequest<KolRecord>(`/kols/${id}`, { method: 'PATCH', body })).data;
}

export async function setKolVisibility(id: string, isVisible: boolean): Promise<KolRecord> {
  return (
    await apiRequest<KolRecord>(`/kols/${id}/visibility`, {
      method: 'PATCH',
      body: { isVisible },
    })
  ).data;
}

// ---------------------------------------------------------------------------
// 文件（两阶段上传）
// ---------------------------------------------------------------------------

const MIME_BY_EXTENSION: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
  '.mp4': 'video/mp4',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/** 不信任浏览器给出的 MIME，按扩展名推导（服务端仍会二次校验） */
export function resolveUploadMimeType(fileName: string): string {
  const index = fileName.lastIndexOf('.');
  if (index === -1) {
    return 'application/octet-stream';
  }
  return MIME_BY_EXTENSION[fileName.slice(index).toLowerCase()] ?? 'application/octet-stream';
}

/**
 * OPT-15：分块增量 SHA-256（优先 Web Worker，不阻塞主线程）。
 * 不再调用 `file.arrayBuffer()`，2GB 文件也不会整块读入内存；
 * 结果与 WebCrypto / 服务端 `sha256` 完全一致（见 sha256-chunked.test.ts）。
 */
export function sha256HexOfFile(file: File, options: ChunkedHashOptions = {}): Promise<string> {
  if (typeof Worker === 'undefined') {
    return sha256HexOfBlob(file, options);
  }
  return new Promise<string>((resolve, reject) => {
    const worker = new Worker(new URL('./sha256.worker.ts', import.meta.url), { type: 'module' });
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      options.signal?.removeEventListener('abort', onAbort);
      worker.terminate();
    };
    const onAbort = (): void => {
      finish();
      reject(new AbortError());
    };
    if (options.signal?.aborted) {
      finish();
      reject(new AbortError());
      return;
    }
    options.signal?.addEventListener('abort', onAbort, { once: true });
    worker.onmessage = (event: MessageEvent) => {
      const data = event.data as {
        type: 'progress' | 'done' | 'error';
        hex?: string;
        message?: string;
        processed?: number;
        total?: number;
      };
      if (data.type === 'progress') {
        options.onProgress?.(data.processed ?? 0, data.total ?? file.size);
        return;
      }
      if (data.type === 'done' && typeof data.hex === 'string') {
        finish();
        resolve(data.hex);
        return;
      }
      finish();
      reject(new Error(data.message ?? '摘要计算失败'));
    };
    worker.onerror = () => {
      finish();
      reject(new Error('摘要计算失败（Worker 异常）'));
    };
    worker.postMessage({ file, chunkSize: options.chunkSize ?? 4 * 1024 * 1024 });
  });
}

export interface UploadProgress {
  stage: 'HASHING' | 'INTENT' | 'UPLOADING' | 'COMPLETING';
}

/** OPT-15：上传可观察进度 + 可取消 + 网络失败可重试 */
export interface UploadHooks {
  onStage?: (stage: UploadProgress['stage']) => void;
  /** 摘要进度 0–100（与上传字节进度分开显示） */
  onHashProgress?: (percent: number) => void;
  /** 上传字节进度 0–100 */
  onUploadProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

function normalizeHooks(
  hooks?: UploadHooks | ((stage: UploadProgress['stage']) => void),
): UploadHooks {
  return typeof hooks === 'function' ? { onStage: hooks } : (hooks ?? {});
}

/** XHR 上传：fetch 无上传进度事件，且需要可中止 */
function putRawWithProgress(
  url: string,
  file: File,
  contentType: string,
  hooks: UploadHooks,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', contentType);
    const token = getAccessToken();
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        hooks.onUploadProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`上传失败（HTTP ${xhr.status}）`));
    };
    xhr.onerror = () => reject(new Error('网络错误，上传失败'));
    xhr.ontimeout = () => reject(new Error('上传超时，请重试'));
    xhr.onabort = () => reject(new AbortError());
    if (hooks.signal?.aborted) {
      xhr.abort();
      return;
    }
    hooks.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/**
 * 两阶段上传：
 * 1. POST /files/upload-intents（含 sha256）
 * 2. PUT /files/uploads/:token（原始字节）
 * 3. POST /files/uploads/:token/complete（服务端校验大小与摘要后置为 READY）
 */
export async function uploadFileWithIntent(
  file: File,
  purpose: FilePurpose,
  hooksOrStage?: UploadHooks | ((stage: UploadProgress['stage']) => void),
): Promise<UploadedFileMeta> {
  const hooks = normalizeHooks(hooksOrStage);
  hooks.onStage?.('HASHING');
  const sha256 = await sha256HexOfFile(file, {
    ...(hooks.onHashProgress
      ? {
          onProgress: (processed: number, total: number) =>
            hooks.onHashProgress?.(total > 0 ? Math.round((processed / total) * 100) : 100),
        }
      : {}),
    ...(hooks.signal ? { signal: hooks.signal } : {}),
  });
  const mimeType = resolveUploadMimeType(file.name);

  hooks.onStage?.('INTENT');
  const intent = (
    await apiRequest<UploadIntentResult>('/files/upload-intents', {
      method: 'POST',
      body: {
        originalName: file.name,
        purpose,
        mimeType,
        sizeBytes: file.size,
        sha256,
      },
    })
  ).data;

  if (file.size > intent.maxBytes) {
    throw new Error(`文件超过大小限制（最大 ${Math.floor(intent.maxBytes / 1024 / 1024)}MB）`);
  }

  hooks.onStage?.('UPLOADING');
  const uploadUrl = `/api/admin/v1/files/uploads/${intent.uploadToken}`;
  // 网络类失败重试一次（同一上传意图仍有效）；取消与 4xx 不重试
  try {
    await putRawWithProgress(uploadUrl, file, mimeType, hooks);
  } catch (error) {
    if (error instanceof AbortError) throw error;
    const message = error instanceof Error ? error.message : '';
    if (!/网络错误|超时/.test(message)) throw error;
    await putRawWithProgress(uploadUrl, file, mimeType, hooks);
  }

  hooks.onStage?.('COMPLETING');
  return (
    await apiRequest<UploadedFileMeta>(`/files/uploads/${intent.uploadToken}/complete`, {
      method: 'POST',
    })
  ).data;
}

// ---------------------------------------------------------------------------
// 内容
// ---------------------------------------------------------------------------

export interface ContentListQuery extends PageQuery {
  type?: ContentType;
  state?: string;
  /** 库存状态筛选（仅 BOOK）：有可借 / 无可借（有册但全借出等）/ 未挂实体册 */
  stockStatus?: 'HAS_AVAILABLE' | 'NO_AVAILABLE' | 'NO_COPIES';
  keyword?: string;
  tagId?: string;
  kolId?: string;
}

export async function listContents(
  query: ContentListQuery = {},
): Promise<Paginated<ContentListRow>> {
  const result = await apiRequest<ContentListRow[]>('/contents', {
    query: {
      page: query.page,
      pageSize: query.pageSize,
      type: query.type,
      state: query.state,
      stockStatus: query.stockStatus,
      keyword: query.keyword,
      tagId: query.tagId,
      kolId: query.kolId,
    },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function getContent(id: string): Promise<ContentDetail> {
  return (await apiRequest<ContentDetail>(`/contents/${id}`)).data;
}

export async function createContent(body: ContentCreatePayload): Promise<ContentDetail> {
  return (await apiRequest<ContentDetail>('/contents', { method: 'POST', body })).data;
}

export async function updateContent(
  id: string,
  body: ContentUpdatePayload,
): Promise<ContentDetail> {
  return (await apiRequest<ContentDetail>(`/contents/${id}`, { method: 'PATCH', body })).data;
}

export async function validateContent(id: string): Promise<ContentValidationResult> {
  return (await apiRequest<ContentValidationResult>(`/contents/${id}/validation`)).data;
}

export async function publishContent(id: string, scheduledAt?: string): Promise<ContentDetail> {
  return (
    await apiRequest<ContentDetail>(`/contents/${id}/publish`, {
      method: 'POST',
      body: scheduledAt ? { scheduledAt } : {},
    })
  ).data;
}

export async function unpublishContent(id: string, reason: string): Promise<ContentDetail> {
  return (
    await apiRequest<ContentDetail>(`/contents/${id}/unpublish`, {
      method: 'POST',
      body: { reason },
    })
  ).data;
}

export async function restoreContentDraft(id: string, reason: string): Promise<ContentDetail> {
  return (
    await apiRequest<ContentDetail>(`/contents/${id}/restore-draft`, {
      method: 'POST',
      body: { reason },
    })
  ).data;
}

export async function deleteContent(id: string, reason: string): Promise<ContentDetail> {
  return (
    await apiRequest<ContentDetail>(`/contents/${id}`, { method: 'DELETE', body: { reason } })
  ).data;
}

export async function searchUsers(keyword: string): Promise<UserSearchResult[]> {
  return (await apiRequest<UserSearchResult[]>('/contents/users/search', { query: { keyword } }))
    .data;
}

export async function bindContentResource(
  contentId: string,
  body: { fileId: string; kind: ContentResourceKind; label?: string; isPrimary?: boolean },
): Promise<unknown> {
  return (await apiRequest<unknown>(`/contents/${contentId}/resources`, { method: 'POST', body }))
    .data;
}

export async function unbindContentResource(
  contentId: string,
  resourceId: string,
): Promise<unknown> {
  return (
    await apiRequest<unknown>(`/contents/${contentId}/resources/${resourceId}`, {
      method: 'DELETE',
    })
  ).data;
}

export async function setPrimaryContentResource(
  contentId: string,
  resourceId: string,
): Promise<unknown> {
  return (
    await apiRequest<unknown>(`/contents/${contentId}/resources/${resourceId}/primary`, {
      method: 'POST',
    })
  ).data;
}

export async function getResourceSignedUrl(
  contentId: string,
  resourceId: string,
  mode: 'preview' | 'download',
): Promise<Record<string, unknown>> {
  return (
    await apiRequest<Record<string, unknown>>(
      `/contents/${contentId}/resources/${resourceId}/${mode}`,
    )
  ).data;
}

export async function grantContentAccess(
  contentId: string,
  body: { liyuNo: string; scope: ContentAccessScope; expiresAt?: string },
): Promise<unknown> {
  return (await apiRequest<unknown>(`/contents/${contentId}/access`, { method: 'POST', body }))
    .data;
}

export async function revokeContentAccess(contentId: string, accessId: string): Promise<unknown> {
  return (
    await apiRequest<unknown>(`/contents/${contentId}/access/${accessId}`, { method: 'DELETE' })
  ).data;
}

// ---------------------------------------------------------------------------
// 专题
// ---------------------------------------------------------------------------

export async function listTopics(
  query: PageQuery & { keyword?: string } = {},
): Promise<Paginated<TopicListRow>> {
  const result = await apiRequest<TopicListRow[]>('/topics', {
    query: { page: query.page, pageSize: query.pageSize, keyword: query.keyword },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export interface TopicUpsertPayload {
  title: string;
  subtitle?: string;
  summary?: string;
  coverUrl?: string;
  themeStyle?: TopicThemeStyle;
  topicType?: import('./types').TopicType;
  isFeatured?: boolean;
  featuredOrder?: number;
  sortOrder?: number;
}

export async function createTopic(body: TopicUpsertPayload): Promise<TopicDetail> {
  return (await apiRequest<TopicDetail>('/topics', { method: 'POST', body })).data;
}

export async function getTopic(id: string): Promise<TopicDetail> {
  return (await apiRequest<TopicDetail>(`/topics/${id}`)).data;
}

export async function updateTopic(
  id: string,
  body: Partial<TopicUpsertPayload>,
): Promise<TopicDetail> {
  return (await apiRequest<TopicDetail>(`/topics/${id}`, { method: 'PATCH', body })).data;
}

export async function publishTopic(id: string): Promise<TopicDetail> {
  return (await apiRequest<TopicDetail>(`/topics/${id}/publish`, { method: 'POST' })).data;
}

export async function unpublishTopic(id: string, reason: string): Promise<TopicDetail> {
  return (
    await apiRequest<TopicDetail>(`/topics/${id}/unpublish`, { method: 'POST', body: { reason } })
  ).data;
}

export async function deleteTopic(id: string, reason: string): Promise<TopicDetail> {
  return (await apiRequest<TopicDetail>(`/topics/${id}`, { method: 'DELETE', body: { reason } }))
    .data;
}

export async function setTopicContents(id: string, ids: string[]): Promise<TopicDetail> {
  return (
    await apiRequest<TopicDetail>(`/topics/${id}/contents`, { method: 'PATCH', body: { ids } })
  ).data;
}

export async function setTopicKols(id: string, ids: string[]): Promise<TopicDetail> {
  return (await apiRequest<TopicDetail>(`/topics/${id}/kols`, { method: 'PATCH', body: { ids } }))
    .data;
}

// ---------------------------------------------------------------------------
// 首页配置
// ---------------------------------------------------------------------------

export async function listHomeModules(): Promise<HomeModuleRow[]> {
  return (await apiRequest<HomeModuleRow[]>('/home/modules')).data;
}

export async function updateHomeModule(
  type: HomeModuleType,
  body: { isEnabled?: boolean; title?: string; sortOrder?: number; strategy?: unknown },
): Promise<HomeModuleRow> {
  return (await apiRequest<HomeModuleRow>(`/home/modules/${type}`, { method: 'PATCH', body })).data;
}

export async function setHomeModuleItems(
  type: HomeModuleType,
  items: HomeItemInput[],
): Promise<HomeModuleRow[]> {
  return (
    await apiRequest<HomeModuleRow[]>(`/home/modules/${type}/items`, {
      method: 'PUT',
      body: { items },
    })
  ).data;
}

// ---------------------------------------------------------------------------
// 批量导入
// ---------------------------------------------------------------------------

export async function listImportJobs(query: PageQuery = {}): Promise<Paginated<ImportJobRow>> {
  const result = await apiRequest<ImportJobRow[]>('/imports', {
    query: { page: query.page, pageSize: query.pageSize },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function getImportJob(id: string): Promise<ImportJobDetail> {
  return (await apiRequest<ImportJobDetail>(`/imports/${id}`)).data;
}

export async function createImportJob(fileId: string): Promise<ImportJobDetail> {
  return (await apiRequest<ImportJobDetail>('/imports', { method: 'POST', body: { fileId } })).data;
}

export async function validateImportJob(id: string): Promise<ImportJobDetail> {
  return (await apiRequest<ImportJobDetail>(`/imports/${id}/validate`, { method: 'POST' })).data;
}

export async function confirmImportJob(id: string): Promise<ImportJobDetail> {
  return (await apiRequest<ImportJobDetail>(`/imports/${id}/confirm`, { method: 'POST' })).data;
}

export async function retryImportJob(id: string): Promise<ImportJobDetail> {
  return (await apiRequest<ImportJobDetail>(`/imports/${id}/retry`, { method: 'POST' })).data;
}

export async function downloadImportTemplate(): Promise<void> {
  await apiDownload('/imports/template', 'liyu-import-template.xlsx');
}

export async function downloadImportErrorReport(jobId: string): Promise<void> {
  await apiDownload(`/imports/${jobId}/error-report`, `import-errors-${jobId}.xlsx`);
}

/** 供表单使用的枚举选项（与后端 DTO 完全一致，避免前后端枚举漂移） */
export const VIEW_ACCESS_OPTIONS: ContentViewAccess[] = [
  'PUBLIC',
  'LOGIN',
  'MEMBER_V1',
  'MEMBER_V2',
  'MEMBER_V3',
  'SPECIFIC_USERS',
];

export const DOWNLOAD_ACCESS_OPTIONS: ContentDownloadAccess[] = [
  'NO_DOWNLOAD',
  'LOGIN',
  'MEMBER_V1',
  'MEMBER_V2',
  'MEMBER_V3',
  'SPECIFIC_USERS',
];

export const PREVIEW_POLICY_OPTIONS: ContentPreviewPolicy[] = ['HIDDEN', 'CARD_ONLY', 'SUMMARY'];

export const COPYRIGHT_OPTIONS: CopyrightStatus[] = [
  'ORIGINAL',
  'AUTHORIZED_REPOST',
  'PUBLIC_SOURCE_REFERENCE',
  'PENDING_CONFIRMATION',
];

export const CONTENT_TYPE_OPTIONS: ContentType[] = [
  'ARTICLE',
  'VIDEO',
  'RESEARCH',
  'REPLAY',
  'BOOK',
];

export interface BatchUnpublishResult {
  total: number;
  success: number;
  failed: number;
  results: Array<{ id: string; ok: boolean; message?: string }>;
}

/** 批量下架：服务端逐条事务处理并返回明细 */
export async function batchUnpublishContents(
  ids: string[],
  reason: string,
): Promise<BatchUnpublishResult> {
  const response = await apiRequest<BatchUnpublishResult>('/contents/batch/unpublish', {
    method: 'POST',
    body: { ids, reason },
  });
  return response.data;
}

// ---------------------------------------------------------------------------
// O3 运营闭环 A-01/A-02：活动票种
// 后端：admin-ticket-type.controller.ts（金额单位一律「分」）
// ---------------------------------------------------------------------------

/** 活动票种列表（含服务端计算的展示状态、可售余量与占位库存） */
export async function listActivityTicketTypes(
  activityId: string,
): Promise<AdminTicketTypeListResult> {
  return (await apiRequest<AdminTicketTypeListResult>(`/activities/${activityId}/ticket-types`))
    .data;
}

export async function createTicketType(
  activityId: string,
  body: TicketTypeUpsertPayload,
): Promise<AdminTicketTypeRow> {
  return (
    await apiRequest<AdminTicketTypeRow>(`/activities/${activityId}/ticket-types`, {
      method: 'POST',
      body,
    })
  ).data;
}

export async function updateTicketType(
  id: string,
  body: TicketTypeUpsertPayload,
): Promise<AdminTicketTypeRow> {
  return (await apiRequest<AdminTicketTypeRow>(`/ticket-types/${id}`, { method: 'PATCH', body }))
    .data;
}

/** 软删除（不做物理删除）；reason 写入审计 */
export async function deleteTicketType(
  id: string,
  reason: string,
): Promise<{ id: string; deleted: boolean }> {
  return (
    await apiRequest<{ id: string; deleted: boolean }>(`/ticket-types/${id}`, {
      method: 'DELETE',
      body: { reason },
    })
  ).data;
}

/** 上/下架：只阻止新建订单，不影响已支付报名 */
export async function setTicketTypeActive(
  id: string,
  isActive: boolean,
): Promise<AdminTicketTypeRow> {
  return (
    await apiRequest<AdminTicketTypeRow>(
      `/ticket-types/${id}/${isActive ? 'activate' : 'deactivate'}`,
      { method: 'POST' },
    )
  ).data;
}

// ---------------------------------------------------------------------------
// O3 运营闭环 A-08/A-09/A-10：会员套餐与权益
// 后端：admin-membership-plan.controller.ts（仅 SUPER_ADMIN；金额单位「分」）
// ---------------------------------------------------------------------------

/** 会员套餐列表（含权益与历史订单数；不含已删除） */
export async function listMembershipPlans(): Promise<AdminMembershipPlan[]> {
  const result = await apiRequest<{ items: AdminMembershipPlan[] }>('/membership-plans');
  return result.data.items;
}

export async function createMembershipPlan(
  body: MembershipPlanUpsertPayload,
): Promise<AdminMembershipPlan> {
  return (await apiRequest<AdminMembershipPlan>('/membership-plans', { method: 'POST', body }))
    .data;
}

/** 更新套餐（code 不可修改；改价不影响历史订单快照） */
export async function updateMembershipPlan(
  id: string,
  body: MembershipPlanUpsertPayload,
): Promise<AdminMembershipPlan> {
  return (
    await apiRequest<AdminMembershipPlan>(`/membership-plans/${id}`, { method: 'PATCH', body })
  ).data;
}

/** 整组替换套餐权益：未提交的权益被置为显式关闭，不物理删除 */
export async function replaceMembershipPlanBenefits(
  id: string,
  benefits: PlanBenefitReplaceInput[],
): Promise<AdminMembershipPlan> {
  return (
    await apiRequest<AdminMembershipPlan>(`/membership-plans/${id}/benefits`, {
      method: 'PUT',
      body: { benefits },
    })
  ).data;
}

/** 上/下架：只阻止新购，不影响既有会员与历史订单 */
export async function setMembershipPlanActive(
  id: string,
  isActive: boolean,
): Promise<AdminMembershipPlan> {
  return (
    await apiRequest<AdminMembershipPlan>(
      `/membership-plans/${id}/${isActive ? 'activate' : 'deactivate'}`,
      { method: 'POST' },
    )
  ).data;
}

/** 软删除套餐；reason 写入审计（历史会员/订单保留 planId 快照） */
export async function deleteMembershipPlan(
  id: string,
  reason: string,
): Promise<{ id: string; deleted: boolean }> {
  return (
    await apiRequest<{ id: string; deleted: boolean }>(`/membership-plans/${id}`, {
      method: 'DELETE',
      body: { reason },
    })
  ).data;
}

// ---------------------------------------------------------------------------
// O3 运营闭环 A-06/A-07：退款运营处置
// 后端：refund.controller.ts（AdminRefundController）
// 只做处置（approve 推进渠道退款 / reject 置 FAILED），不直接改金额。
// ---------------------------------------------------------------------------

/** 退款列表；分页字段在 data 内（非响应信封 meta） */
export async function listRefunds(
  query: AdminRefundListQuery = {},
): Promise<AdminRefundListResult> {
  const result = await apiRequest<AdminRefundListResult>('/refunds', {
    query: {
      status: query.status,
      orderNo: query.orderNo,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
  return result.data;
}

/** 放行 / 重试退款（幂等）；note 可选并写入审计 */
export async function approveRefund(id: string, note?: string): Promise<AdminRefundRow> {
  return (
    await apiRequest<AdminRefundRow>(`/refunds/${id}/approve`, {
      method: 'POST',
      body: note ? { note } : {},
    })
  ).data;
}

/** 驳回退款：必填原因，服务端置 FAILED + failureReason，订单保持 PAID */
export async function rejectRefund(id: string, reason: string): Promise<AdminRefundRow> {
  return (
    await apiRequest<AdminRefundRow>(`/refunds/${id}/reject`, {
      method: 'POST',
      body: { reason },
    })
  ).data;
}

// ---------------------------------------------------------------------------
// B7 借阅域（33 号《图书借阅方案 V1.1》§5.3 A-15~A-20）
// 后端：apps/api/src/modules/borrowing/admin-*.controller.ts
//
// 说明：这些列表接口把 { items, total, page, pageSize } 放在响应信封的 data 内
// （与 /refunds 同口径），不占用 envelope.meta，故直接返回 result.data。
// 写命令的 Guard 角色见 §5.3：A-15~A-18 为 LIBRARY_OPERATOR + SUPER_ADMIN，
// A-19/A-20 为 SUPER_ADMIN；前端只做按钮禁用提示，最终以服务端 Guard 为准。
// ---------------------------------------------------------------------------

/** A-15 实体册列表（bookContentId / status 过滤） */
export async function listBookCopies(
  query: AdminBookCopyListQuery = {},
): Promise<Paginated<AdminBookCopyRow>> {
  const result = await apiRequest<Paginated<AdminBookCopyRow>>('/book-copies', {
    query: {
      bookContentId: query.bookContentId,
      status: query.status,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
  return result.data;
}

/** A-15 新增实体册：quantity 支持 1–50 批量录入，copyNo 缺省由服务端自动递增 */
export async function createBookCopy(
  body: AdminBookCopyPayload,
): Promise<{ items: AdminBookCopyRow[]; count: number }> {
  return (
    await apiRequest<{ items: AdminBookCopyRow[]; count: number }>('/book-copies', {
      method: 'POST',
      body,
    })
  ).data;
}

/** 作者列表（keyword 模糊搜姓名/英文名） */
export async function listAuthors(
  query: { keyword?: string; limit?: number } = {},
): Promise<{ items: AdminAuthorRow[] }> {
  return (
    await apiRequest<{ items: AdminAuthorRow[] }>('/authors', { query })
  ).data;
}

/** 新建作者 */
export async function createAuthor(body: AdminAuthorPayload): Promise<AdminAuthorRow> {
  return (await apiRequest<AdminAuthorRow>('/authors', { method: 'POST', body })).data;
}

/** 更新作者 */
export async function updateAuthor(id: string, body: AdminAuthorPayload): Promise<AdminAuthorRow> {
  return (await apiRequest<AdminAuthorRow>(`/authors/${id}`, { method: 'PATCH', body })).data;
}

/** 捐赠人列表（keyword 模糊搜姓名，含捐赠册数） */
export async function listDonors(
  query: { keyword?: string; limit?: number } = {},
): Promise<{ items: AdminDonorRow[] }> {
  return (
    await apiRequest<{ items: AdminDonorRow[] }>('/donors', { query })
  ).data;
}

/** 新建捐赠人 */
export async function createDonor(body: AdminDonorPayload): Promise<AdminDonorRow> {
  return (await apiRequest<AdminDonorRow>('/donors', { method: 'POST', body })).data;
}

/** 更新捐赠人 */
export async function updateDonor(id: string, body: AdminDonorPayload): Promise<AdminDonorRow> {
  return (await apiRequest<AdminDonorRow>(`/donors/${id}`, { method: 'PATCH', body })).data;
}

/** 快捷新建图书书目（书名 + 作者即可，创建为草稿） */
export async function quickCreateBook(body: {
  title: string;
  authorId?: string;
  authorName?: string;
  coverUrl?: string;
}): Promise<QuickCreatedBook> {
  return (
    await apiRequest<QuickCreatedBook>('/contents/books/quick-create', { method: 'POST', body })
  ).data;
}

/** A-15 更新实体册（可借/维修/丢失）；MAINTENANCE / LOST 必须给 remark */
export async function updateBookCopy(
  id: string,
  body: AdminBookCopyPayload,
): Promise<AdminBookCopyRow> {
  return (await apiRequest<AdminBookCopyRow>(`/book-copies/${id}`, { method: 'PATCH', body })).data;
}

/** A-16 借阅单列表（status=ACTIVE|OVERDUE|单个状态；keyword 单号/手机号/姓名） */
export async function listBorrowings(
  query: AdminBorrowingListQuery = {},
): Promise<AdminBorrowingListResult> {
  const result = await apiRequest<AdminBorrowingListResult>('/borrowings', {
    query: {
      status: query.status,
      keyword: query.keyword,
      pickupMethod: query.pickupMethod,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
  return result.data;
}

/** A-17 逾期看板（status=BORROWED AND dueAt < now，按 overdueDays DESC；该端点不返回联系方式） */
export async function listOverdueBorrowings(
  query: PageQuery = {},
): Promise<AdminBorrowingListResult> {
  const result = await apiRequest<AdminBorrowingListResult>('/borrowings/overdue', {
    query: { page: query.page, pageSize: query.pageSize },
  });
  return result.data;
}

/** A-16 审核通过：必须分配实体册（bookCopyId），同事务占库 */
export async function approveBorrowing(
  id: string,
  body: AdminApproveBorrowingPayload,
): Promise<AdminBorrowingRow> {
  return (
    await apiRequest<AdminBorrowingRow>(`/borrowings/${id}/approve`, { method: 'POST', body })
  ).data;
}

/** 快递发货：仅 EXPRESS+APPROVED；提交即进 BORROWED 并起算借期（不可逆） */
export async function shipBorrowing(
  id: string,
  body: { courierCompany: string; trackingNo: string },
): Promise<AdminBorrowingRow> {
  return (
    await apiRequest<AdminBorrowingRow>(`/borrowings/${id}/ship`, { method: 'POST', body })
  ).data;
}

/** 修改发货物流：仅 BORROWED 且已发货的快递单（写审计） */
export async function updateBorrowingExpress(
  id: string,
  body: { courierCompany?: string; trackingNo?: string },
): Promise<AdminBorrowingRow> {
  return (
    await apiRequest<AdminBorrowingRow>(`/borrowings/${id}/express`, { method: 'POST', body })
  ).data;
}

/** A-16 驳回：reviewNote 必填（§4.1 T7） */
export async function rejectBorrowing(id: string, reviewNote: string): Promise<AdminBorrowingRow> {
  return (
    await apiRequest<AdminBorrowingRow>(`/borrowings/${id}/reject`, {
      method: 'POST',
      body: { reviewNote },
    })
  ).data;
}

/** A-16 确认领取：同事务生成 pickedUpAt/dueAt，副本 → BORROWED（不可逆） */
export async function handoverBorrowing(id: string): Promise<AdminBorrowingRow> {
  return (
    await apiRequest<AdminBorrowingRow>(`/borrowings/${id}/handover`, { method: 'POST', body: {} })
  ).data;
}

/** A-16 确认归还：同事务副本 → AVAILABLE 并释放给预约队列（不可逆） */
export async function confirmReturnBorrowing(
  id: string,
  reason?: string,
): Promise<AdminBorrowingRow> {
  return (
    await apiRequest<AdminBorrowingRow>(`/borrowings/${id}/confirm-return`, {
      method: 'POST',
      body: reason ? { reason } : {},
    })
  ).data;
}

/** A-16 后台取消：仅 APPROVED（closedReason=PICKUP_TIMEOUT） */
export async function cancelBorrowing(id: string, reason?: string): Promise<AdminBorrowingRow> {
  return (
    await apiRequest<AdminBorrowingRow>(`/borrowings/${id}/cancel`, {
      method: 'POST',
      body: reason ? { reason } : {},
    })
  ).data;
}

/**
 * A-16 手动触发借阅周期任务（逾期刷新 + 预约维护）。
 * `dryRun` 服务端暂未实现，前端不发送该字段。
 */
export async function runBorrowingTasks(
  task: AdminBorrowingTaskKind = 'all',
): Promise<AdminBorrowingTaskResult> {
  return (
    await apiRequest<AdminBorrowingTaskResult>('/borrowings/tasks/run', {
      method: 'POST',
      body: task === 'all' ? {} : { task },
    })
  ).data;
}

/** A-18 预约列表（status / bookContentId 过滤） */
export async function listReservations(
  query: AdminReservationListQuery = {},
): Promise<Paginated<AdminReservationRow>> {
  const result = await apiRequest<Paginated<AdminReservationRow>>('/reservations', {
    query: {
      status: query.status,
      bookContentId: query.bookContentId,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
  return result.data;
}

/** A-18 人工补发到书通知（WAITING → NOTIFIED） */
export async function notifyReservation(id: string): Promise<AdminReservationRow> {
  return (
    await apiRequest<AdminReservationRow>(`/reservations/${id}/notify`, {
      method: 'POST',
      body: {},
    })
  ).data;
}

/** A-18 后台取消预约（WAITING / NOTIFIED → CANCELLED） */
export async function cancelReservation(id: string, reason?: string): Promise<AdminReservationRow> {
  return (
    await apiRequest<AdminReservationRow>(`/reservations/${id}/cancel`, {
      method: 'POST',
      body: reason ? { reason } : {},
    })
  ).data;
}

/** A-20 读取借阅政策（系统单行配置；含 env 兜底） */
export async function getBorrowPolicy(): Promise<AdminBorrowPolicy> {
  return (await apiRequest<AdminBorrowPolicy>('/borrow-policy')).data;
}

/** A-20 更新借阅政策（写 AuditLog=UPDATE_BORROW_POLICY；保存即失效 60s 缓存） */
export async function updateBorrowPolicy(
  body: AdminBorrowPolicyUpdatePayload,
): Promise<AdminBorrowPolicy> {
  return (await apiRequest<AdminBorrowPolicy>('/borrow-policy', { method: 'PUT', body })).data;
}

/** A-19 领取地点列表（含停用；按 sortOrder / 创建时间排序） */
export async function listPickupLocations(): Promise<AdminPickupLocationRow[]> {
  return (await apiRequest<{ items: AdminPickupLocationRow[] }>('/pickup-locations')).data.items;
}

/** A-19 新增领取地点（写 AuditLog=PICKUP_LOCATION_CREATE） */
export async function createPickupLocation(
  body: AdminPickupLocationPayload,
): Promise<AdminPickupLocationRow> {
  return (await apiRequest<AdminPickupLocationRow>('/pickup-locations', { method: 'POST', body }))
    .data;
}

/** A-19 更新 / 停用领取地点（不做物理删除） */
export async function updatePickupLocation(
  id: string,
  body: AdminPickupLocationPayload,
): Promise<AdminPickupLocationRow> {
  return (
    await apiRequest<AdminPickupLocationRow>(`/pickup-locations/${id}`, { method: 'PATCH', body })
  ).data;
}

/* ------------------------------ KOL 认证申请 / 投稿审核（V1） ------------------------------ */


/** 服务端直接返回 { items,total,page,pageSize }（无 meta 信封），与 toPaginated 分开处理 */
interface DirectPaginated<T> {
  items: T;
  total: number;
  page: number;
  pageSize: number;
}

export async function listKolApplications(
  query: PageQuery & { status?: string; keyword?: string } = {},
): Promise<Paginated<KolApplicationRecord>> {
  const result = await apiRequest<DirectPaginated<KolApplicationRecord[]>>('/kol-applications', {
    query: { page: query.page, pageSize: query.pageSize, status: query.status, keyword: query.keyword },
  });
  return {
    items: result.data.items ?? [],
    total: result.data.total ?? 0,
    page: result.data.page ?? 1,
    pageSize: result.data.pageSize ?? query.pageSize ?? 20,
  };
}

export async function getKolApplication(id: string): Promise<KolApplicationRecord> {
  return (await apiRequest<KolApplicationRecord>(`/kol-applications/${id}`)).data;
}

export interface KolApplicationApprovePayload {
  certificationName?: string;
  certificationNote?: string;
  tags?: string[];
  expertise?: string[];
  isVisible?: boolean;
}

export async function approveKolApplication(
  id: string,
  body: KolApplicationApprovePayload = {},
): Promise<KolApplicationRecord> {
  return (await apiRequest<KolApplicationRecord>(`/kol-applications/${id}/approve`, { method: 'POST', body }))
    .data;
}

export async function rejectKolApplication(
  id: string,
  rejectReason: string,
): Promise<KolApplicationRecord> {
  return (
    await apiRequest<KolApplicationRecord>(`/kol-applications/${id}/reject`, {
      method: 'POST',
      body: { rejectReason },
    })
  ).data;
}

export async function listKolInvites(page = 1, pageSize = 20): Promise<Paginated<KolInviteRecord>> {
  const result = await apiRequest<DirectPaginated<KolInviteRecord[]>>('/kol-applications/invites', {
    query: { page, pageSize },
  });
  return {
    items: result.data.items ?? [],
    total: result.data.total ?? 0,
    page: result.data.page ?? 1,
    pageSize: result.data.pageSize ?? pageSize,
  };
}

export async function createKolInvite(
  note?: string,
  expiresInDays?: number,
): Promise<KolInviteRecord> {
  return (
    await apiRequest<KolInviteRecord>('/kol-applications/invites', {
      method: 'POST',
      body: { note, expiresInDays },
    })
  ).data;
}

export async function listKolArticles(
  query: PageQuery & { status?: string } = {},
): Promise<Paginated<KolArticleAuditRow>> {
  const result = await apiRequest<DirectPaginated<KolArticleAuditRow[]>>('/kol-articles', {
    query: { page: query.page, pageSize: query.pageSize, status: query.status },
  });
  return {
    items: result.data.items ?? [],
    total: result.data.total ?? 0,
    page: result.data.page ?? 1,
    pageSize: result.data.pageSize ?? query.pageSize ?? 20,
  };
}

export async function approveKolArticle(id: string): Promise<KolArticleAuditRow> {
  return (await apiRequest<KolArticleAuditRow>(`/kol-articles/${id}/approve`, { method: 'POST' })).data;
}

export async function rejectKolArticle(id: string, rejectReason: string): Promise<KolArticleAuditRow> {
  return (
    await apiRequest<KolArticleAuditRow>(`/kol-articles/${id}/reject`, {
      method: 'POST',
      body: { rejectReason },
    })
  ).data;
}
